// File Metadata:
// Version: 1.05
// Created: 2025-04-09 22:17:01
// Authors: [Your Name], Assistant, GitHub Copilot
// Description: App routing with role-aware protected routes and PWA functionality
// Last Modified: 2026-05-31
// Filename: App.jsx

import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AppLayout from './components/layout/AppLayout';
import AuthLoader from './features/Auth/components/AuthLoader';
import ProtectedRoute from './features/Auth/components/ProtectedRoute';
import RoutePreserver from './components/RoutePreserver';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { ThemeContextProvider } from './contexts/ThemeContext';
import LoadingScreen from './components/common/LoadingScreen';
import { BackgroundTimer } from './features/Timer/BackgroundTimer';

// Lazy loaded components
const Home = lazy(() => import('./pages/Home'));
const Landing = lazy(() => import('./pages/Landing'));
const Login = lazy(() => import('./features/Auth/Login'));
const Signup = lazy(() => import('./features/Auth/Signup'));
const Chores = lazy(() => import('./features/Chores/ChoresPage'));
const ParentDashboard = lazy(() => import('./features/ParentDashboard/ParentDashboard'));
const Checklists = lazy(() => import('./features/Checklist/Checklist'));
const Todos = lazy(() => import('./features/Todos/Todos'));
const Rewards = lazy(() => import('./features/Rewards/Rewards'));
const Profile = lazy(() => import('./features/Profile/Profile'));
const VerifiedPage = lazy(() => import('./features/Auth/VerifiedPage'));
const ResetPassword = lazy(() => import('./features/Auth/ResetPassword'));
const Chat = lazy(() => import('./features/Chat/Chat'));
const TestTokenRefresh = lazy(() => import('./components/TestTokenRefresh'));
const PointsExample = lazy(() => import('./components/examples/PointsExample'));
const ScannerPage = lazy(() => import('./features/Scanner/ScannerPage'));
const Games = lazy(() => import('./features/Games/GameGallery'));
const FinanceDashboardPage = lazy(() => import('./features/FinanceDashboard/FinanceDashboardPage'));
const FinanceOverviewPage = lazy(() => import('./features/FinanceDashboard/FinanceOverviewPage'));
const FinanceTransactionsPage = lazy(() => import('./features/FinanceDashboard/FinanceTransactionsPage'));
const FinanceRulesPage = lazy(() => import('./features/FinanceDashboard/FinanceRulesPage'));
const FinanceAccountsPage = lazy(() => import('./features/FinanceDashboard/FinanceAccountsPage'));
const FinanceBudgetsPage = lazy(() => import('./features/FinanceDashboard/FinanceBudgetsPage'));
const FinanceCategoriesPage = lazy(() => import('./features/FinanceDashboard/FinanceCategoriesPage'));
const Timer = lazy(() => import('./features/Timer/Timer'));
// Route guard components — defined outside App to maintain stable references
const AuthenticatedRoute = ({ children, redirectTo = "/login" }) => {
  const user = useSelector((state) => state.user.data);
  const authStatus = useSelector((state) => state.user.status);
  if (authStatus === 'loading') return <LoadingScreen />;
  if (user) return children;
  return <Navigate to={redirectTo} />;
};

const HomeRoute = () => {
  const user = useSelector((state) => state.user.data);
  const authStatus = useSelector((state) => state.user.status);
  if (authStatus === 'loading') return <LoadingScreen />;
  if (user) return <Home />;
  return <Landing />;
};

const UnauthenticatedRoute = ({ children, redirectTo = "/" }) => {
  const user = useSelector((state) => state.user.data);
  const authStatus = useSelector((state) => state.user.status);
  if (authStatus === 'loading') return <LoadingScreen />;
  if (user) return <Navigate to={redirectTo} />;
  return children;
};


function App() {

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js').then(
          (registration) => {
            console.log('ServiceWorker registration successful with scope: ', registration.scope);
          },
          (error) => {
            console.log('ServiceWorker registration failed: ', error);
          }
        );
      });
    }
  }, []);

  return (
    <ThemeContextProvider>
      <BrowserRouter>
        <RoutePreserver>
          <AppLayout>
            {/* Background timer runs independently of route changes */}
            <BackgroundTimer />
            <AuthLoader>
              <Suspense fallback={<LoadingScreen />}>
                <Routes>
                  <Route path="/" element={<HomeRoute />} />
                  <Route path="/login" element={<UnauthenticatedRoute><Login /></UnauthenticatedRoute>} />
                  <Route path="/signup" element={<UnauthenticatedRoute><Signup /></UnauthenticatedRoute>} />
                  <Route path="/verified" element={<VerifiedPage />} />
                  <Route path="/reset-password" element={<ResetPassword />} /> 
                  <Route
                    path="/dashboard"
                    element={<ProtectedRoute role={['parent', 'admin']}><ParentDashboard /></ProtectedRoute>} />
                  <Route path="/chores" element={<AuthenticatedRoute><Chores /></AuthenticatedRoute>} />
                  <Route path="/checklists" element={<ProtectedRoute role={['child', 'parent', 'admin']}><Checklists /></ProtectedRoute>} />
                  <Route path="/todos" element={<AuthenticatedRoute><Todos /></AuthenticatedRoute>} />
                  <Route path="/rewards" element={<AuthenticatedRoute><Rewards /></AuthenticatedRoute>} />
                  <Route path="/profile" element={<AuthenticatedRoute><Profile /></AuthenticatedRoute>} />
                  <Route path="/chat" element={<AuthenticatedRoute><Chat /></AuthenticatedRoute>} />
                  <Route path="/debug-auth" element={<AuthenticatedRoute><TestTokenRefresh /></AuthenticatedRoute>} />
                  <Route path="/debug-points" element={<AuthenticatedRoute><PointsExample /></AuthenticatedRoute>} />
                  <Route path="/scanner" element={<AuthenticatedRoute><ScannerPage /></AuthenticatedRoute>} />
                  <Route path="/games" element={<AuthenticatedRoute><Games /></AuthenticatedRoute>} />
                  <Route path="/finance" element={<AuthenticatedRoute><FinanceOverviewPage /></AuthenticatedRoute>} />
                  <Route path="/finance/import" element={<AuthenticatedRoute><FinanceDashboardPage /></AuthenticatedRoute>} />
                  <Route path="/finance/transactions" element={<AuthenticatedRoute><FinanceTransactionsPage /></AuthenticatedRoute>} />
                  <Route path="/finance/rules" element={<AuthenticatedRoute><FinanceRulesPage /></AuthenticatedRoute>} />
                  <Route path="/finance/budgets" element={<AuthenticatedRoute><FinanceBudgetsPage /></AuthenticatedRoute>} />
                  <Route path="/finance/accounts" element={<AuthenticatedRoute><FinanceAccountsPage /></AuthenticatedRoute>} />
                  <Route path="/finance/categories" element={<AuthenticatedRoute><FinanceCategoriesPage /></AuthenticatedRoute>} />
                  <Route path="/timer" element={<AuthenticatedRoute><Timer /></AuthenticatedRoute>} />
                </Routes>
              </Suspense>
            </AuthLoader>
            {/* PWA Install Prompt will show only when conditions are right for installation */}
            <PWAInstallPrompt />
          </AppLayout>
        </RoutePreserver>
      </BrowserRouter>
    </ThemeContextProvider>
  );
}

export default App;